import React, { useState, useEffect, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { Button, TextField } from "@mui/material";
import {
  QrCodeIcon,
  MagnifyingGlassIcon,
  DocumentArrowUpIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ModernProductSelectorV2 from "../../../utils/Selector/ModernProductSelectorV2";
import { fetchProducts } from "../../../../api/product";
import toast from "react-hot-toast";
import QRCodeScanner from "../../../utils/QRCodeScanner";
import { CountCSVImportDialog } from "./CountCSVImportDialog";
import { warehouseStockApi } from "../../../../api/inventory/warehouse-stock";
import CountAddItemsDialog from "./CountAddItemsDialog";
import { addItemToCount } from "../../../../api/inventory/count_items";

// Helper function to format numbers with Norwegian locale
const formatNumber = (value) => {
  return (value || 0).toLocaleString("nb-NO", { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

/**
 * Add Product Dialog for Inventory Counts
 * Supports manual search, QR scan, and CSV/Excel import
 */
export const CountAddProductDialog = ({
  open,
  onClose,
  onProductSelected,
  onImportComplete,
  countId,
}) => {
  const { t } = useTranslation();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [note, setNote] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isAddingProducts, setIsAddingProducts] = useState(false);
  
  // Separate dialog states
  const [showBulkAddDialog, setShowBulkAddDialog] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [manualSearchMode, setManualSearchMode] = useState(false);
  
  // QR scan form state
  const [scannedProduct, setScannedProduct] = useState(null);
  const [qrQuantity, setQrQuantity] = useState(1);
  const [qrNote, setQrNote] = useState("");
  const [scannedProductStock, setScannedProductStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  
  // Product stock for selected products
  const [selectedProductsStock, setSelectedProductsStock] = useState({});

  // Reset state when main dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedProducts([]);
      setNote("");
      setShowBulkAddDialog(false);
      setShowQrScanner(false);
      setShowCSVImport(false);
      setManualSearchMode(false);
      setScannedProduct(null);
      setQrQuantity(1);
      setQrNote("");
      setScannedProductStock(null);
      setSelectedProductsStock({});
    }
  }, [open]);

  // Fetch stock when scanned product changes
  useEffect(() => {
    if (scannedProduct?.id) {
      fetchProductStock(scannedProduct.id, setScannedProductStock);
    }
  }, [scannedProduct?.id]);

  // Fetch stock for selected products
  useEffect(() => {
    if (selectedProducts.length > 0) {
      selectedProducts.forEach((product) => {
        if (product.id && !selectedProductsStock[product.id]) {
          fetchProductStock(product.id, (stock) => {
            setSelectedProductsStock((prev) => ({
              ...prev,
              [product.id]: stock,
            }));
          });
        }
      });
    }
  }, [selectedProducts]);

  const fetchProductStock = async (productId, setStockCallback) => {
    if (!productId) return;
    setLoadingStock(true);
    try {
      const response = await warehouseStockApi.getProductStock(productId);
      if (response?.success && response?.data) {
        setStockCallback(response.data);
      }
    } catch (error) {
      console.error("Error fetching product stock:", error);
    } finally {
      setLoadingStock(false);
    }
  };

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error(t("inventory.count.qr.required"));
      return;
    }

    if (isSearching) return;
    setIsSearching(true);

    try {
      let response = null;
      
      if (/^\d+$/.test(scannedCode.trim())) {
        try {
          response = await fetchProducts({
            ids: scannedCode.trim(),
            page: 1,
            per_page: 1,
            fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
          });
        } catch (error) {
          console.log("Product ID search failed, trying query search");
        }
      }
      
      if (!response || !response.data || response.data.length === 0) {
        response = await fetchProducts({
          query: scannedCode.trim(),
          page: 1,
          per_page: 10,
          fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
        });
      }
      
      if (response && response.data && response.data.length > 0) {
        const product = response.data[0];
        // Set scanned product to show form
        setScannedProduct(product);
        setQrQuantity(1);
        setQrNote("");
      } else {
        toast.error(t("inventory.count.product.not_found"));
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error(t("inventory.count.product.search_error"));
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error(t("inventory.count.product.select_required"));
      return;
    }

    if (isAddingProducts) return; // Prevent multiple clicks

    setIsAddingProducts(true);
    try {
      // Add each selected product with its quantity
      for (const product of selectedProducts) {
        const quantity = product.quantity || 1;
        await onProductSelected(product, { 
          note,
          quantity: quantity
        });
      }
      // Reset and close
      setSelectedProducts([]);
      setNote("");
      setManualSearchMode(false);
      onClose();
      toast.success(t("inventory.count.products_added", { count: selectedProducts.length }));
    } catch (error) {
      console.error("Error adding products:", error);
      toast.error(t("inventory.count.product.add_error"));
      // Don't close dialog on error so user can try again
    } finally {
      setIsAddingProducts(false);
    }
  };

  const handleAddScannedProduct = async () => {
    if (!scannedProduct) {
      return;
    }

    if (!qrQuantity || qrQuantity <= 0) {
      toast.error(t("inventory.count.quantity_required"));
      return;
    }

    try {
      // Add product with quantity and note
      await onProductSelected(scannedProduct, { 
        note: qrNote,
        quantity: qrQuantity 
      });
      
      // Reset and close QR scanner and main dialog
      setScannedProduct(null);
      setQrQuantity(1);
      setQrNote("");
      setShowQrScanner(false);
      onClose(); // Close the main dialog
      toast.success(t("inventory.count.product.added"));
    } catch (error) {
      console.error("Error adding scanned product:", error);
      toast.error(t("inventory.count.product.add_error"));
      // Don't close dialog on error so user can try again
    }
  };

  return (
    <>
      {/* Main Dialog */}
      <Transition.Root show={open && !showBulkAddDialog && !showQrScanner && !showCSVImport && !manualSearchMode} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    {t("inventory.count.add_item")}
                  </Dialog.Title>

                  {selectedProducts.length === 0 ? (
                    // No products selected - show action buttons
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setManualSearchMode(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          {t("inventory.count.select_product")}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowBulkAddDialog(true);
                          setTimeout(() => {
                            // Close main dialog after bulk dialog opens
                          }, 100);
                        }}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          {t("inventory.count.add_products_bulk")}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowQrScanner(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <QrCodeIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          {t("inventory.count.scan_qr")}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCSVImport(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <DocumentArrowUpIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          {t("inventory.count.import_csv")}
                        </span>
                      </button>
                      <div className="mt-4 flex justify-end">
                        <Button variant="outlined" onClick={onClose}>
                          {t("Close")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Products selected - show products and note field
                    <div className="space-y-4">
                      <div className="text-sm font-medium text-gray-700">
                        {t("inventory.count.selected_products")} ({selectedProducts.length}):
                      </div>
                      {selectedProducts.map((product) => {
                        const productStock = selectedProductsStock[product.id];
                        return (
                          <div key={product.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="font-medium text-gray-900">
                              {product.displayTitle || product.title || `Produkt ${product.id}`}
                            </div>
                            {product.item_number && (
                              <div className="text-sm text-gray-500 mt-1">
                                {t("inventory.count.item_number")}: {product.item_number}
                              </div>
                            )}
                            {product.purchase_price && (
                              <div className="text-sm text-gray-500 mt-1">
                                {t("inventory.count.purchase_price")}: {formatNumber(product.purchase_price)}
                              </div>
                            )}
                            {product.sales_price && (
                              <div className="text-sm text-gray-500 mt-1">
                                {t("inventory.count.sales_price")}: {formatNumber(product.sales_price)}
                              </div>
                            )}
                            
                            {/* Product Stock Information */}
                            {productStock && (
                              <div className="mt-3 pt-3 border-t border-blue-200">
                                <div className="text-xs font-medium text-gray-700 mb-1">
                                  {t("inventory.count.product_stock")}
                                </div>
                                <div className="grid grid-cols-2 gap-1 text-xs">
                                  <div>
                                    <span className="text-gray-600">{t("inventory.count.tracked_quantity")}:</span>
                                    <span className="ml-1 font-medium text-gray-900">
                                      {formatNumber(productStock.tracked_quantity || 0)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t("inventory.count.available_quantity")}:</span>
                                    <span className="ml-1 font-medium text-gray-900">
                                      {formatNumber(productStock.available_quantity || 0)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t("inventory.count.incoming_quantity")}:</span>
                                    <span className="ml-1 font-medium text-green-600">
                                      {formatNumber(productStock.incoming_quantity || 0)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t("inventory.count.outgoing_quantity")}:</span>
                                    <span className="ml-1 font-medium text-red-600">
                                      {formatNumber(productStock.outgoing_quantity || 0)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedProducts(selectedProducts.filter((p) => p.id !== product.id));
                                setSelectedProductsStock((prev) => {
                                  const updated = { ...prev };
                                  delete updated[product.id];
                                  return updated;
                                });
                              }}
                              className="mt-2 text-sm text-red-600 hover:text-red-800"
                            >
                              {t("inventory.count.remove")}
                            </button>
                          </div>
                        );
                      })}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("inventory.count.note_optional")}
                        </label>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder={t("inventory.count.note_placeholder")}
                        />
                      </div>

                      <div className="flex justify-end space-x-2 mt-4">
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedProducts([]);
                            setNote("");
                          }}
                        >
                          {t("inventory.count.reset")}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleAddProducts}
                          disabled={isSearching || isAddingProducts}
                        >
                          {isAddingProducts 
                            ? t("inventory.count.adding_products") 
                            : `${t("inventory.count.add_products")} (${selectedProducts.length})`}
                        </Button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Manual Search Mode with ModernProductSelectorV2 */}
      {manualSearchMode && (
        <Transition.Root show={manualSearchMode} as={Fragment}>
          <Dialog as="div" className="relative z-50" static onClose={() => {
            setManualSearchMode(false);
            setSelectedProducts([]);
            setNote("");
          }}>
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className={`flex min-h-full items-center justify-center text-center ${
                manualSearchMode 
                  ? 'p-0 sm:p-4' 
                  : 'p-4'
              }`}>
                <Transition.Child
                  as="div"
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className={`relative transform overflow-hidden bg-white text-left shadow-xl transition-all w-full max-w-md md:max-w-6xl lg:max-w-7xl ${
                    manualSearchMode
                      ? 'h-screen max-h-screen rounded-none sm:rounded-lg sm:h-[90vh] sm:max-h-[90vh] flex flex-col min-h-0'
                      : 'rounded-md max-h-[85vh]'
                  } ${manualSearchMode ? 'p-0 sm:px-4 sm:py-4' : 'px-4 py-4'}`}>
                    <div className={`flex items-center justify-between flex-shrink-0 ${manualSearchMode ? 'px-4 pt-4 pb-2 sm:px-0 sm:pt-0 sm:pb-0' : 'mb-4'}`}>
                      <Dialog.Title className="text-lg font-medium text-gray-900">
                        {t("inventory.count.select_product")}
                      </Dialog.Title>
                      <button
                        type="button"
                        onClick={() => {
                          setManualSearchMode(false);
                          setSelectedProducts([]);
                          setNote("");
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="Lukk"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                        <ModernProductSelectorV2
                          open={true}
                          selectedProducts={selectedProducts}
                          onChange={(products) => {
                            setSelectedProducts(products);
                          }}
                          onClose={() => {
                            setManualSearchMode(false);
                          }}
                          multiple={true}
                          filters={[]}
                          showHeader={false}
                          showWarehouseOnlyDefault={true}
                          showQuantityInput={true}
                        />
                      </div>
                      {selectedProducts.length > 0 && (
                        <div className="flex-shrink-0 mt-2 px-4 pb-4 sm:px-0 sm:pb-0 flex space-x-2 border-t pt-4">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t("inventory.count.note_optional")}
                            </label>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              variant="outlined"
                              size="small"
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder={t("inventory.count.note_placeholder")}
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setSelectedProducts([]);
                                setNote("");
                              }}
                            >
                              {t("inventory.count.reset")}
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => {
                                handleAddProducts();
                                setManualSearchMode(false);
                              }}
                              disabled={isAddingProducts}
                            >
                              {isAddingProducts 
                                ? t("inventory.count.adding_products") 
                                : `${t("inventory.count.add_products")} (${selectedProducts.length})`}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}

      {/* Bulk Add Items Dialog */}
      <CountAddItemsDialog
        open={showBulkAddDialog}
        onClose={() => {
          setShowBulkAddDialog(false);
        }}
        onSave={async (itemsToAdd) => {
          try {
            // Add each item individually
            for (const item of itemsToAdd) {
              await addItemToCount(countId, item);
            }
            toast.success(t("inventory.count.items_added", { count: itemsToAdd.length }));
            setShowBulkAddDialog(false);
            if (onImportComplete) {
              onImportComplete(itemsToAdd);
            }
          } catch (error) {
            console.error("Error adding items:", error);
            toast.error(t("inventory.count.items_add_error"));
          }
        }}
        countId={countId}
        onSubDialogStateChange={(hasOpen) => {
          // Notify parent about sub-dialog state if needed
        }}
      />

      {/* QR Scanner Dialog */}
      <Transition.Root show={showQrScanner} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowQrScanner(false)}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {scannedProduct ? t("inventory.count.add_scanned_product") : t("inventory.count.scan_qr")}
                    </Dialog.Title>
                    <button
                      onClick={() => {
                        setScannedProduct(null);
                        setQrQuantity(1);
                        setQrNote("");
                        setShowQrScanner(false);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  {scannedProduct ? (
                    // Show form for scanned product
                    <div className="space-y-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="font-medium text-gray-900">
                          {scannedProduct.displayTitle || scannedProduct.title || `Produkt ${scannedProduct.id}`}
                        </div>
                        {scannedProduct.item_number && (
                          <div className="text-sm text-gray-500 mt-1">
                            {t("inventory.count.item_number")}: {scannedProduct.item_number}
                          </div>
                        )}
                        {scannedProduct.purchase_price && (
                          <div className="text-sm text-gray-500 mt-1">
                            {t("inventory.count.purchase_price")}: {formatNumber(scannedProduct.purchase_price)}
                          </div>
                        )}
                        {scannedProduct.sales_price && (
                          <div className="text-sm text-gray-500 mt-1">
                            {t("inventory.count.sales_price")}: {formatNumber(scannedProduct.sales_price)}
                          </div>
                        )}
                      </div>

                      {/* Product Stock Information */}
                      {scannedProductStock && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="text-sm font-medium text-gray-700 mb-2">
                            {t("inventory.count.product_stock")}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">{t("inventory.count.tracked_quantity")}:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {formatNumber(scannedProductStock.tracked_quantity || 0)}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">{t("inventory.count.available_quantity")}:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {formatNumber(scannedProductStock.available_quantity || 0)}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">{t("inventory.count.incoming_quantity")}:</span>
                              <span className="ml-2 font-medium text-green-600">
                                {formatNumber(scannedProductStock.incoming_quantity || 0)}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">{t("inventory.count.outgoing_quantity")}:</span>
                              <span className="ml-2 font-medium text-red-600">
                                {formatNumber(scannedProductStock.outgoing_quantity || 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      {loadingStock && (
                        <div className="text-sm text-gray-500 text-center py-2">
                          {t("Loading...")}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("inventory.count.quantity")} *
                        </label>
                        <TextField
                          fullWidth
                          type="number"
                          variant="outlined"
                          value={qrQuantity}
                          onChange={(e) => setQrQuantity(parseFloat(e.target.value) || 1)}
                          inputProps={{ min: 0.01, step: 0.01 }}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("inventory.count.note_optional")}
                        </label>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          value={qrNote}
                          onChange={(e) => setQrNote(e.target.value)}
                          placeholder={t("inventory.count.note_placeholder")}
                        />
                      </div>

                      <div className="flex justify-end space-x-2 mt-6">
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setScannedProduct(null);
                            setQrQuantity(1);
                            setQrNote("");
                          }}
                        >
                          {t("inventory.count.scan_another")}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleAddScannedProduct}
                          disabled={isSearching || !qrQuantity || qrQuantity <= 0}
                        >
                          {t("inventory.count.add_product")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Show QR scanner
                    <QRCodeScanner
                      open={true}
                      onScan={handleQrCodeScan}
                      onBack={() => setShowQrScanner(false)}
                      placeholder={t("inventory.count.qr.placeholder")}
                      isSearching={isSearching}
                    />
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* CSV Import Dialog */}
      <CountCSVImportDialog
        open={showCSVImport}
        onClose={() => setShowCSVImport(false)}
        onImportComplete={(importedItems) => {
          // Close CSV import dialog
          setShowCSVImport(false);
          // Call parent's import complete callback to refresh the list
          if (onImportComplete) {
            onImportComplete(importedItems);
          }
          // Close main dialog after successful import
          // Note: CountCSVImportDialog already shows success toast
          onClose();
        }}
        countId={countId}
      />
    </>
  );
};



